<template>
  <div>
    <div class="d-flex flex-column align-end">
      <div
        v-if="mode !== 'script' && youtubeVideoId"
        class="d-block mb-4"
        :style="$vuetify.breakpoint.xs ? 'width: 100%' : 'width: 100%; max-width: 300px;'"
      >
        <LiteYouTubeEmbed
          title="YouTube video embed"
          :id="youtubeVideoId"
          :params="`controls=1&playsinline=1&iv_load_policy=3&rel=0&hl=${options.language}`"
        />
      </div>
      <div class="option-user-box-wrapper" :style="$vuetify.breakpoint.xs ? 'max-width: 90%;' : 'max-width: 80%'">
        <div class="user-box rounded-xl px-6 py-2 mb-4">
          {{
            mode === "ideas"
              ? $t("generateUniqueVideoIdeasFor")
              : mode === "script" && options.scriptTemplate === "outline"
              ? $t("generateVideoScriptOutline")
              : mode === "script" && options.scriptTemplate === "short"
              ? $t("generateShortVideoScript")
              : mode === "script"
              ? $t("generateVideoScript")
              : mode === "rewriteScript"
              ? $t("rewriteScriptOfThis")
              : mode === "title"
              ? $t("generateHighlyOptimizedTitleForThis")
              : mode === "description"
              ? $t("generateHighlyOptimizedDescriptionForThis")
              : mode === "tags"
              ? $t("generateHighlyOptimizedVideoTagsForThis")
              : mode === "chapters"
              ? $t("generateVideoChaptersForThis")
              : ""
          }}<a
            v-if="mode !== 'script'"
            :href="mode === 'ideas' ? `https://www.youtube.com/${options.channelHandle}` : options.videoUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-decoration-none primary--text font-weight-bold"
          >
            {{ mode === "ideas" ? options.channelHandle : $t("video").toLowerCase() }}</a
          >.
        </div>
        <div
          v-if="mode === 'script' && options.scriptTemplate === 'general' && options.videoUrl"
          class="user-box rounded-xl px-6 py-2 mb-4"
        >
          {{ $t("useThis")
          }}<a
            :href="options.videoUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-decoration-none primary--text font-weight-bold"
          >
            {{ $t("video").toLowerCase() }}</a
          >
          {{ $t("asInspirationToMatchToneToMyBrand").toLowerCase() }}.
        </div>
        <div
          v-if="
            !['script', 'rewriteScript', 'tags', 'chapters'].includes(mode) &&
            (options.inspirationChannelHandles?.length > 0 || options.trendingInRegion)
          "
          class="user-box rounded-xl px-6 py-2 mb-4"
        >
          {{ $t("drawInspirationFrom") }}
          <span v-if="options.inspirationChannelHandles?.length > 0">
            <a
              v-for="(handle, index) in options.inspirationChannelHandles"
              :key="handle"
              :href="`https://www.youtube.com/${handle}`"
              target="_blank"
              rel="noopener noreferrer"
              class="text-decoration-none primary--text font-weight-bold"
              >{{ handle
              }}<span
                v-if="index !== options.inspirationChannelHandles.length - 1"
                class="default--text font-weight-regular"
                >,
              </span>
            </a>
            <span v-if="options.trendingInRegion">
              {{ $t("and") }} {{ $t("whatsTrendingIn") }} {{ options.trendingInRegion }}</span
            >.
          </span>
          <span v-else>{{ $t("whatsTrendingIn") }} {{ options.trendingInRegion }}.</span>
        </div>
        <div
          v-else-if="mode === 'script' && options.scriptTemplate !== 'short'"
          class="user-box rounded-xl px-6 py-2 mb-4"
        >
          {{ $t("aimForScriptLengthOfWords", { words: abbreviateNumber(options.numberOfWords) }) }} ({{
            $t("approximatelyXMinutes", { minutes: wordsToSpeakingMinutes(options.numberOfWords) })
          }}).
        </div>
      </div>
    </div>

    <div
      v-for="(msg, idx) in messages"
      :key="idx"
      :class="['chat-message', msg.role === 'user' ? 'user-msg' : 'assistant-msg']"
    >
      <div v-if="msg.role === 'user'">
        <v-alert v-if="msg.reply" color="transparent" dense class="caption text--disabled mb-0">
          {{ truncateText(msg.reply) }}
          <template v-slot:prepend>
            <v-icon class="mr-4 mb-auto text--disabled">mdi-arrow-right-bottom</v-icon>
          </template>
        </v-alert>
        <div class="user-box rounded-xl px-6 py-2 mb-4" v-html="formatContent(msg.content)"></div>
      </div>

      <div v-else :style="$vuetify.breakpoint.xs ? 'max-width: 90%' : 'max-width: 80%'">
        <bubble-menu
          class="bubble-menu"
          :editor="editors[idx]"
          :tippy-options="{ duration: 100 }"
          :shouldShow="shouldShowBubbleMenu"
        >
          <v-btn icon small dark @click="reply(idx)">
            <v-icon>mdi-format-quote-close</v-icon>
          </v-btn>
        </bubble-menu>
        <editor-content :editor="editors[idx]" :class="$vuetify.theme.dark ? 'theme--dark' : 'theme--light'" />
      </div>
    </div>

    <div v-if="waitingForAssistantMessage" class="d-flex align-center mt-4 mb-2">
      <div class="typing">
        <span class="circle scaling"></span>
        <span class="circle scaling"></span>
        <span class="circle scaling"></span>
      </div>
      <span class="ml-2 caption text--disabled">{{ progressMessage }}</span>
    </div>
  </div>
</template>

<script>
import { isTextSelection } from "@tiptap/core";
import { Editor, EditorContent, BubbleMenu } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import { DOMSerializer } from "prosemirror-model";
import showdown from "showdown";
import LiteYouTubeEmbed from "vue-lite-youtube-embed";
import "vue-lite-youtube-embed/style.css";

export default {
  props: ["mode", "options", "messages", "waitingForAssistantMessage", "progressMessage"],
  name: "ChatMessages",
  components: {
    EditorContent,
    BubbleMenu,
    LiteYouTubeEmbed,
  },
  data() {
    return {
      converter: null,
      editors: [],
    };
  },
  computed: {
    youtubeVideoId() {
      try {
        const videoUrl = this.options.videoUrl;
        const youtubeRegex = /https:\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)([\w\-_]*)(&(amp;)?[\w?=]*)?/;
        if (youtubeRegex.test(videoUrl)) return videoUrl.match(youtubeRegex)[1];
        return null;
      } catch {
        return null;
      }
    },
  },
  watch: {
    messages: {
      handler(newMessages, oldMessages) {
        if (!this.converter) this.converter = new showdown.Converter({ noHeaderId: true });

        const newEditors = [];

        newMessages.forEach((msg, idx) => {
          if (msg.role === "assistant") {
            // Is there an editor for this message index and same content already?
            const oldEditor = this.editors[idx];
            const oldMsg = oldMessages && oldMessages[idx];
            // If exists and content is same, reuse
            if (oldEditor && oldMsg && oldMsg.role === "assistant" && oldMsg.content === msg.content) {
              newEditors.push(oldEditor);
            } else {
              // Otherwise, destroy old (if any), create new
              if (oldEditor) oldEditor.destroy();
              const html = this.converter.makeHtml(msg.content || "");
              const editor = new Editor({
                editable: true,
                content: html,
                extensions: [StarterKit.configure({ document: true })],
              });
              newEditors.push(editor);
            }
          } else {
            // User messages don't need an editor
            newEditors.push(null);
          }
        });

        // Destroy any old editors that are no longer needed (from removed messages)
        if (this.editors) {
          for (let i = newMessages.length; i < this.editors.length; i++) {
            if (this.editors[i]) this.editors[i].destroy();
          }
        }

        this.editors = newEditors;

        setTimeout(() => this.setTiptapsContenteditableFalse(), 250);
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.editors.forEach((ed) => ed?.destroy?.());
  },
  methods: {
    formatContent(content) {
      return content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
    },
    setTiptapsContenteditableFalse() {
      const tiptaps = document.querySelectorAll(".tiptap");
      tiptaps.forEach((div) => {
        div.setAttribute("contenteditable", "false");
      });
    },
    shouldShowBubbleMenu: ({ view, state, from, to }) => {
      const { doc, selection } = state;
      const { empty } = selection;

      const isEmptyTextBlock = !doc.textBetween(from, to).length && isTextSelection(state.selection);
      const hasEditorFocus = view.hasFocus();

      if (!hasEditorFocus || empty || isEmptyTextBlock) return false;
      return true;
    },
    reply(idx) {
      this.setTiptapsContenteditableFalse();
      const editor = this.editors[idx];
      if (!editor) return;
      const cleanMarkdown = (md) => {
        md = md.replace(/<br\s*\/?>/gi, "");
        md = md.replace(/<!--[\s\S]*?-->/g, "");
        md = md.replace(/^\s*[\r\n]/gm, "");
        return md.trim();
      };
      const slice = editor.state.selection.content();
      const serializer = DOMSerializer.fromSchema(editor.schema);
      const fragment = serializer.serializeFragment(slice.content);
      const div = document.createElement("div");
      div.appendChild(fragment);

      // Convert to Markdown
      let md = this.converter.makeMd(div.innerHTML);

      // Clean up unwanted stuff as before
      md = cleanMarkdown(md);

      // Remove numbered list markers (e.g., "1. ", "2. " at line start)
      md = md.replace(/^\d+\.\s+/gm, "");

      this.$emit("reply", md);
      return md;
    },
    abbreviateNumber(num) {
      if (num >= 1000) {
        let formatted = (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1);
        return `${formatted}k`;
      }
      return num.toString();
    },
    wordsToSpeakingMinutes(numberOfWords) {
      const count = Number(numberOfWords) || 0;
      return +(count / 145).toFixed();
    },
    truncateText(str) {
      const charLimit = this.$vuetify.breakpoint.xs ? 72 : 300;
      if (str.length <= charLimit) return str;
      else return str.slice(0, charLimit).trim() + "...";
    },
  },
};
</script>

<style scoped>
.bubble-menu {
  display: flex;
  background-color: black;
  padding: 2px 12px;
  border-radius: 24px;
}

:deep(.tiptap) {
  outline: none;
}

:deep(.tiptap) h3 {
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.6;
  letter-spacing: 0.0125em;
}

:deep(.tiptap) p {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.03125em;
}

:deep(.tiptap) hr {
  display: block;
  flex: 1 1 0px;
  max-width: 100%;
  height: 0;
  max-height: 0;
  border: solid;
  border-width: thin 0 0;
  transition: inherit;
  margin: 28px 0 28px 0;
}

.theme--light :deep(.tiptap) hr {
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--dark :deep(.tiptap) hr {
  border-color: hsla(0, 0%, 100%, 0.12);
}

:deep(.tiptap) ul,
:deep(.tiptap) ol {
  /* margin-top: 1.25em;
  margin-bottom: 1.75em; */
  margin-left: 1.62em;
  padding-left: 0.6em;
}

:deep(.tiptap) li {
  margin-bottom: 0.86em;
  font-size: 1em;
  padding-left: 0.15em;
}

:deep(.tiptap) li .list-marker {
  font-weight: bold;
  margin-right: 0.5em;
}

:deep(.tiptap) strong {
  font-weight: 700;
}

:deep(.tiptap) br {
  line-height: 1.25;
}

.chat-message {
  display: flex;
  flex-direction: column;
}
.chat-message > div {
  max-width: 80%;
}
@media (max-width: 600px) {
  .chat-message > div {
    max-width: 90%;
  }
}
.user-msg {
  align-items: flex-end;
}
.assistant-msg {
  align-items: flex-start;
  margin-top: 16px;
  margin-bottom: 16px;
}
.option-user-box-wrapper {
  display: grid;
  justify-items: end;
}
.user-box {
  width: fit-content;
  float: right;
}
.theme--light .user-box {
  background: rgba(0, 0, 0, 0.06);
}
.theme--dark .user-box {
  background: hsla(0, 0%, 100%, 0.08);
}
.theme--light .default--text {
  color: rgba(0, 0, 0, 0.87);
}
.theme--dark .default--text {
  color: #fff;
}

.typing {
  display: block;
  width: 72px;
  height: 40px;
  border-radius: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.theme--light .typing {
  background: rgba(0, 0, 0, 0.06);
}

.theme--dark .typing {
  background: hsla(0, 0%, 100%, 0.08);
}

.circle {
  display: block;
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background-color: #8d8d8d;
  margin: 3px;
}

.circle.scaling {
  animation: typing 1000ms ease-in-out infinite;
  animation-delay: 3600ms;
}

.circle:nth-child(1) {
  animation-delay: 0ms;
}

.circle:nth-child(2) {
  animation-delay: 333ms;
}

.circle:nth-child(3) {
  animation-delay: 666ms;
}

@keyframes typing {
  0% {
    transform: scale(1);
  }
  33% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.4);
  }
  100% {
    transform: scale(1);
  }
}
</style>
